import { VIDEO_NOTIFICATION_HISTORY_CAP } from './constants';
import {
  EnqueueVideoGenerationNotificationArgs,
  TiktokVideoNotificationItem,
  VideoGenerationNotificationItem,
  VideoNotificationItem,
  VideoNotificationStatus,
  VideoNotificationsSettingValue,
} from './types';

const TERMINAL_STATUSES = new Set<VideoNotificationStatus>(['ready', 'failed']);

function isVideoNotificationStatus(value: unknown): value is VideoNotificationStatus {
  return value === 'pending' || value === 'ready' || value === 'failed';
}

function isTiktokVideoNotificationItem(value: unknown): value is TiktokVideoNotificationItem {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const item = value as Record<string, unknown>;

  return (
    (item.kind === undefined || item.kind === 'tiktok-animation') &&
    typeof item.id === 'string' &&
    typeof item.adId === 'string' &&
    typeof item.adPrettyId === 'string' &&
    typeof item.campaignPrettyId === 'string' &&
    (typeof item.adPoolPrettyId === 'string' || item.adPoolPrettyId === null) &&
    (typeof item.animationId === 'string' || item.animationId === null) &&
    isVideoNotificationStatus(item.status) &&
    typeof item.read === 'boolean' &&
    typeof item.createdAt === 'number' &&
    typeof item.updatedAt === 'number'
  );
}

function isVideoGenerationNotificationItem(value: unknown): value is VideoGenerationNotificationItem {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const item = value as Record<string, unknown>;

  return (
    item.kind === 'video-generation' &&
    typeof item.id === 'string' &&
    typeof item.generationId === 'string' &&
    typeof item.advertiserId === 'string' &&
    typeof item.advertiserPrettyId === 'string' &&
    typeof item.campaignPrettyId === 'string' &&
    (typeof item.videoFileId === 'string' || item.videoFileId === null) &&
    isVideoNotificationStatus(item.status) &&
    typeof item.read === 'boolean' &&
    typeof item.createdAt === 'number' &&
    typeof item.updatedAt === 'number'
  );
}

function isVideoNotificationItem(value: unknown): value is VideoNotificationItem {
  return isTiktokVideoNotificationItem(value) || isVideoGenerationNotificationItem(value);
}

export function isVideoGenerationNotification(item: VideoNotificationItem): item is VideoGenerationNotificationItem {
  return item.kind === 'video-generation';
}

export function parseVideoNotificationsSetting(value: unknown): VideoNotificationItem[] {
  if (typeof value !== 'object' || value === null || !('items' in value)) {
    return [];
  }

  const { items } = value as VideoNotificationsSettingValue;

  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter(isVideoNotificationItem);
}

export function mapAnimationStatus(status: string | null | undefined): VideoNotificationStatus | null {
  if (status === 'PENDING' || status === 'NOT_SUBMITTED') {
    return 'pending';
  }

  if (status === 'RESOLVED') {
    return 'ready';
  }

  if (status === 'REJECTED') {
    return 'failed';
  }

  return null;
}

export function mapGenerationStatus(status: string | null | undefined): VideoNotificationStatus | null {
  if (status === 'PENDING' || status === 'NOT_SUBMITTED') {
    return 'pending';
  }

  if (status === 'RESOLVED') {
    return 'ready';
  }

  if (status === 'REJECTED' || status === 'FAILED') {
    return 'failed';
  }

  return null;
}

export function resolveVideoNotificationConflict<T extends VideoNotificationItem>(first: T, second: T): T {
  const firstIsTerminal = TERMINAL_STATUSES.has(first.status);
  const secondIsTerminal = TERMINAL_STATUSES.has(second.status);

  let preferred: T;

  if (firstIsTerminal !== secondIsTerminal) {
    preferred = firstIsTerminal ? first : second;
  } else {
    preferred = first.updatedAt >= second.updatedAt ? first : second;
  }

  return {
    ...preferred,
    read: firstIsTerminal && secondIsTerminal ? first.read || second.read : preferred.read,
  };
}

export function mergeVideoNotificationItems(
  local: VideoNotificationItem[],
  remote: VideoNotificationItem[],
): VideoNotificationItem[] {
  const byId = new Map<string, VideoNotificationItem>();

  [...remote, ...local].forEach((item) => {
    const existing = byId.get(item.id);
    byId.set(item.id, existing ? resolveVideoNotificationConflict(existing, item) : item);
  });

  return Array.from(byId.values());
}

export function capVideoNotificationHistory(items: VideoNotificationItem[]): VideoNotificationItem[] {
  const pending = items.filter((item) => item.status === 'pending');
  const terminal = items
    .filter((item) => item.status !== 'pending')
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, VIDEO_NOTIFICATION_HISTORY_CAP);

  return [...pending, ...terminal];
}

export function getUnreadVideoNotificationCount(items: VideoNotificationItem[]): number {
  return items.filter((item) => item.status !== 'pending' && !item.read).length;
}

export function getVisibleVideoNotifications(items: VideoNotificationItem[]): VideoNotificationItem[] {
  return items.filter((item) => item.status !== 'pending').sort((a, b) => b.createdAt - a.createdAt);
}

export function getVideoNotificationAdPath(
  item: Pick<TiktokVideoNotificationItem, 'campaignPrettyId' | 'adPrettyId' | 'adPoolPrettyId'>,
): string {
  const adPoolSegment = item.adPoolPrettyId ?? 'legacy-ad-pool';
  return `/campaign/${item.campaignPrettyId}/ads-and-keywords/${adPoolSegment}/ads/${item.adPrettyId}?action=edit`;
}

export function applyVideoNotificationStatus(
  items: VideoNotificationItem[],
  id: string,
  status: Extract<VideoNotificationStatus, 'ready' | 'failed'>,
  animationId: string | null,
  now: number,
): VideoNotificationItem[] {
  return items.map((item) => {
    if (isVideoGenerationNotification(item) || item.id !== id || item.status !== 'pending') {
      return item;
    }

    return {
      ...item,
      status,
      animationId: animationId ?? item.animationId,
      read: false,
      updatedAt: now,
    };
  });
}

export function applyVideoGenerationStatus(
  items: VideoNotificationItem[],
  id: string,
  status: Extract<VideoNotificationStatus, 'ready' | 'failed'>,
  videoFileId: string | null,
  now: number,
): VideoNotificationItem[] {
  return items.map((item) => {
    if (!isVideoGenerationNotification(item) || item.id !== id || item.status !== 'pending') {
      return item;
    }

    return {
      ...item,
      status,
      videoFileId: videoFileId ?? item.videoFileId,
      read: false,
      updatedAt: now,
    };
  });
}

export function markVideoNotificationRead(
  items: VideoNotificationItem[],
  id: string,
  now: number,
): VideoNotificationItem[] {
  return items.map((item) => (item.id === id ? { ...item, read: true, updatedAt: now } : item));
}

export function dismissVideoNotification(items: VideoNotificationItem[], id: string): VideoNotificationItem[] {
  return items.filter((item) => item.id !== id);
}

export function createPendingVideoNotification(args: {
  id: string;
  adId: string;
  adPrettyId: string;
  campaignPrettyId: string;
  adPoolPrettyId: string | null;
  animationId: string | null;
  now: number;
}): TiktokVideoNotificationItem {
  return {
    id: args.id,
    adId: args.adId,
    adPrettyId: args.adPrettyId,
    campaignPrettyId: args.campaignPrettyId,
    adPoolPrettyId: args.adPoolPrettyId,
    animationId: args.animationId,
    status: 'pending',
    read: true,
    createdAt: args.now,
    updatedAt: args.now,
  };
}

export function createPendingVideoGenerationNotification(
  args: EnqueueVideoGenerationNotificationArgs & { id: string; now: number },
): VideoGenerationNotificationItem {
  return {
    id: args.id,
    kind: 'video-generation',
    generationId: args.generationId,
    advertiserId: args.advertiserId,
    advertiserPrettyId: args.advertiserPrettyId,
    campaignPrettyId: args.campaignPrettyId,
    videoFileId: null,
    status: 'pending',
    read: true,
    createdAt: args.now,
    updatedAt: args.now,
  };
}
